import { Chat, Flag, IdManagement, Link, Time } from '@carbon/icons-react';
import { Banner, LoadingScreen } from 'component/banner';
import { Center, HorizBox, HoverView, Pad, PadBox } from 'component/basics';
import { CTAButton, Tag, TextButton } from 'component/button';
import {
    colorBorderPrimary,
    colorButtonDisabled,
    colorSurfacePrimary,
    colorSurfaceSecondary,
    colorTagHighlight,
    colorTagSuccess,
    colorTextAccent,
    colorTextPrimary,
    colorTextSecondary,
    colorTextWarning,
} from 'component/color';
import { DateTimeDisplay } from 'component/date';
import { Modal } from 'component/modal';
import { DropDownSelector } from 'component/popup';
import { moduleMapQuestion } from 'component/question';
import { Heading, Paragraph, UtilityText } from 'component/text';
import { MODDASHBOARD_CAPABILITY } from 'feature/feature-roles';
import type { BacklinkArticle } from 'feature/topic/reading-list';
import { REPLACE_ZDF_FilterButton } from 'open-modules/zdf/component/button';
import { usePersonasByIds } from 'open-modules/zdf/component/moderation';
import { ParentDatastoreProvider } from 'open-modules/zdf/component/parentdatastore';
import { REPLACE_ZDF_LinkText } from 'open-modules/zdf/component/text';
import type { Tab } from 'open-modules/zdf/tabnavigation';
import { TabBar, TabContent, TabNavigation } from 'open-modules/zdf/tabnavigation';
import { useEffect, useMemo, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import type { ArticleInfo } from 'structure/article';
import { moduleMapTopic } from 'structure/topic';
import { SpacedArray } from 'system/demo';
import { useHasCapability } from 'util/admin';
import {
    DEPRECATED_useModulePublicData,
    DEPRECATED_useModuleUserGlobalData,
    useCollection,
    useDatastore,
    useInstanceKey,
    useModuleMap,
    useModuleObject,
    usePersonaObject,
} from 'util/datastore';
import { EmbeddedInstance } from 'util/instance';
import { useInstanceParams } from 'util/params';
import type { Persona } from 'util/stdtypes';
import { declareTable, t } from 'util/stdtypes';
import { keyToUrl } from 'util/util';

import type { ThreadFilterSelection } from './moddashboardFilters';
import { ModerationFilters, ThreadFilters } from './moddashboardFilters';
import { ModDashboardQueue } from './moddashboardqueue';
import { getQueueSubsetCounts, useFilteredQueues } from './moddashboardqueuehooks';
import type { SecondaryThreadTag } from './moddashboardtypes';
import { ModerationSortingOptions, ModerationSortingOrder, moduleMapQueue } from './moddashboardtypes';

type QuestionOrTopicThread = {
    key: string;
    from: string;
    time: number;
    structureKey: string;
    id?: string; // human-readable identifier
    awaitingDecisionCount?: number;
    commentCount?: number;
    readOnly?: boolean;
    isHeated?: boolean;
    question?: string; // only for questions
    name?: string; // only for topics
    photoUrl?: string; // only for topics?
};

type UseModerationThreadsProps = {
    count?: number;
    sortingOrder?: ModerationSortingOrder;
    filters?: ThreadFilterSelection;
};

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
const useModerationThreads = ({ count = 1, sortingOrder = ModerationSortingOrder.NEWEST, filters }: UseModerationThreadsProps) => {
    const questions = useModuleMap(moduleMapQuestion);
    const questionsWithKeys = Object.entries(questions ?? {}).map(([key, value]) => ({
        key,
        structureKey: 'question',
        ...value,
    }));
    const topics = useModuleMap(moduleMapTopic);
    const topicsWithKeys = Object.entries(topics ?? {}).map(([key, value]) => ({
        key,
        structureKey: 'topic',
        ...value,
    }));

    const awaitingDecisionCounts = DEPRECATED_useModulePublicData('moderation', ['thread_queue_count'], {
        defaultValue: {},
    });

    const [allThreads, setAllThreads] = useState<QuestionOrTopicThread[]>([]);
    const [sortedThreads, setSortedThreads] = useState<QuestionOrTopicThread[]>([]);
    const [remainingThreadCount, setRemainingThreadCount] = useState(0);
    const [newThreadCount, setNewThreadCount] = useState(0);
    const [time, setTime] = useState(sortingOrder === ModerationSortingOrder.NEWEST ? Date.now() : 0);

    // FIXME: This function is too large and should be broken up
    /* eslint-disable-next-line max-lines-per-function */
    useEffect(() => {
        if (!questions && !topics) {
            return;
        }

        // Convert the questions and topics into a list of thread objects with their keys
        let threadObjects: QuestionOrTopicThread[] = [...questionsWithKeys, ...topicsWithKeys];
        threadObjects = threadObjects.map((thread) => {
            const awaitingDecisionCount = awaitingDecisionCounts?.[thread?.key]?.awaiting_decision_count ?? 0;
            return { ...thread, awaitingDecisionCount };
        });
        setAllThreads(threadObjects);

        // Count new threads (those newer than the given timestamp)
        const newThreads =
            sortingOrder === ModerationSortingOrder.NEWEST
                ? threadObjects.filter((thread) =>
                      sortingOrder === ModerationSortingOrder.NEWEST ? thread.time > time : thread.time < time,
                  )
                : [];
        // Set the new thread count
        setNewThreadCount(newThreads.length);

        // Applying filters

        // Multiple filters can be selected at once. In that case, we need to combine the lists of threads.
        let combinedFilteredThreads: QuestionOrTopicThread[] = [];

        if (filters && filters.open) {
            combinedFilteredThreads = combinedFilteredThreads.concat(
                Object.values(threadObjects).filter((thread) => thread.readOnly === false || thread.readOnly === undefined),
            );
        }

        if (filters && filters.closed) {
            combinedFilteredThreads = combinedFilteredThreads.concat(
                Object.values(threadObjects).filter((thread) => thread.readOnly === true),
            );
        }

        if (filters && filters.heated) {
            combinedFilteredThreads = combinedFilteredThreads.concat(
                Object.values(threadObjects).filter((thread) => thread.isHeated === true),
            );
        }

        if (filters && filters.flagged) {
            combinedFilteredThreads = combinedFilteredThreads.concat(
                Object.values(threadObjects).filter((thread) => thread.awaitingDecisionCount != 0),
            );
        }

        // No filter selected = show all threads
        if (!filters || (!filters.open && !filters.closed && !filters.heated && !filters.flagged)) {
            combinedFilteredThreads = Object.values(threadObjects);
        }

        // Remove duplicate threads because some threads can appear under multiple filters, e.g. "heated" and "open".
        const threadsStringified = combinedFilteredThreads.map((thread) => JSON.stringify(thread));
        const threadsStringifiedWithoutDupes = threadsStringified.filter(
            (stringifiedThread, idx) => threadsStringified.indexOf(stringifiedThread) === idx,
        );
        combinedFilteredThreads = threadsStringifiedWithoutDupes.map((stringifiedThread) => JSON.parse(stringifiedThread));

        // Sorting threads

        // Sort by time and filter out threads older than or equal to the timestamp
        const newSortedThreads = combinedFilteredThreads
            .filter((thread) => (sortingOrder === ModerationSortingOrder.OLDEST ? thread.time >= time : thread.time <= time))
            .sort((a, b) => {
                if (sortingOrder === ModerationSortingOrder.NEWEST) {
                    // Sort newest first
                    return b.time - a.time;
                } else if (sortingOrder === ModerationSortingOrder.OLDEST) {
                    // Sort oldest first
                    return a.time - b.time;
                }
                // Fallback (by newest)
                return b.time - a.time;
            });
        setRemainingThreadCount(newSortedThreads.length - count);
        setSortedThreads(newSortedThreads.slice(0, count));
    }, [questions, topics, awaitingDecisionCounts, time, count, filters]);

    useEffect(() => {
        setTime(sortingOrder === ModerationSortingOrder.OLDEST ? 0 : Date.now());
    }, [sortingOrder]);

    return {
        allThreads: allThreads,
        threads: sortedThreads,
        remainingThreadCount: remainingThreadCount,
        newThreadCount: newThreadCount,
    };
};

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ModDashboardThreadsOverview() {
    const datastore = useDatastore();

    const savedSortingOrder: ModerationSortingOrder = DEPRECATED_useModuleUserGlobalData('moderation', ['sortOrder', 'threads'], {
        once: true,
    });
    const [sortingOrder, setSortingOrder] = useState<ModerationSortingOrder>(ModerationSortingOrder.NEWEST);

    const [loadAmount, setLoadAmount] = useState(20);
    const [selectedFilters, setSelectedFilters] = useState<ThreadFilterSelection>({
        [ThreadFilters.OPEN]: false,
        [ThreadFilters.CLOSED]: false,
        [ThreadFilters.HEATED]: false,
        [ThreadFilters.FLAGGED]: false,
    });
    const params = useInstanceParams();

    const { threads, allThreads, remainingThreadCount } = useModerationThreads({
        sortingOrder: sortingOrder ?? savedSortingOrder,
        count: loadAmount,
        filters: selectedFilters,
    });

    const activeThreadsCount = allThreads?.filter((thread) => !thread.readOnly).length;
    const inactiveThreadsCount = allThreads?.filter((thread) => thread.readOnly).length;
    const threadInDetail = allThreads?.find((thread) => thread.key === params?.threadId);
    const heatedThreads = allThreads?.filter((thread) => thread.isHeated).length;
    const flaggedThreads = allThreads?.filter((thread) => thread.awaitingDecisionCount && thread.awaitingDecisionCount > 0).length;

    const authorIds = useMemo(() => {
        return threads?.map((thread) => thread.from) || [];
    }, [threads]);
    const authors: Record<string, Persona> = usePersonasByIds(authorIds);

    const loadMoreThreads = () => {
        setLoadAmount(loadAmount + 20);
    };

    useEffect(() => {
        // Recalculate the heated flag for the threads on initial render
        datastore.callServerAsync('moderationZdf', 'calculateHeated', {});
    }, []);

    const handleSortingOrderChange = (selectedOrder: string) => {
        if (Object.values<string>(ModerationSortingOrder).includes(selectedOrder)) {
            const newSortingOrder = selectedOrder as ModerationSortingOrder;
            datastore.DEPRECATED_setModuleUserGlobal('moderation', ['sortOrder', 'threads'], newSortingOrder);
            setSortingOrder(newSortingOrder);
        }
    };

    const renderedContent = (
        <>
            {params?.modTaskTreeKey && params?.threadId && <ModDashboardThreadTreeView />}
            {threads && threadInDetail && params?.threadId && !params?.modTaskTreeKey && (
                <ParentDatastoreProvider>
                    <EmbeddedInstance
                        features={{ enablemoddashboardthreaddetailview: true }}
                        instanceKey={params.threadId}
                        screenKey={'moderationDetails'}
                        structureKey={threadInDetail?.structureKey}
                    />
                </ParentDatastoreProvider>
            )}
            {!params?.threadId && (
                <View style={{ backgroundColor: colorSurfaceSecondary, flexGrow: 1, flexShrink: 1 }}>
                    <PadBox vert={80}>
                        <Heading label={t('Threads')} type='large' weight='medium' />
                        <Pad size={26} />
                        <SpacedArray horiz pad={8}>
                            <REPLACE_ZDF_FilterButton
                                count={activeThreadsCount || '0'}
                                countColor={colorTextSecondary}
                                label={t('Open Threads')}
                                onPress={() => {
                                    selectedFilters.open
                                        ? setSelectedFilters({ ...selectedFilters, open: false })
                                        : setSelectedFilters({ ...selectedFilters, open: true });
                                }}
                                selected={selectedFilters.open}
                            />

                            <REPLACE_ZDF_FilterButton
                                count={inactiveThreadsCount || '0'}
                                countColor={colorTextSecondary}
                                label={t('Closed Threads')}
                                onPress={() => {
                                    selectedFilters.closed
                                        ? setSelectedFilters({ ...selectedFilters, closed: false })
                                        : setSelectedFilters({ ...selectedFilters, closed: true });
                                }}
                                selected={selectedFilters.closed}
                            />

                            <REPLACE_ZDF_FilterButton
                                count={heatedThreads || '0'}
                                countColor={colorTextSecondary}
                                label={t('Heated Threads')}
                                onPress={() => {
                                    selectedFilters.heated
                                        ? setSelectedFilters({ ...selectedFilters, heated: false })
                                        : setSelectedFilters({ ...selectedFilters, heated: true });
                                }}
                                selected={selectedFilters.heated}
                            />

                            <REPLACE_ZDF_FilterButton
                                count={flaggedThreads || '0'}
                                countColor={colorTextSecondary}
                                label={t('Threads with flagged comments')}
                                onPress={() => {
                                    selectedFilters.flagged
                                        ? setSelectedFilters({ ...selectedFilters, flagged: false })
                                        : setSelectedFilters({ ...selectedFilters, flagged: true });
                                }}
                                selected={selectedFilters.flagged}
                            />
                        </SpacedArray>
                        <View>
                            <Pad size={26} />
                            <DropDownSelector
                                onChange={handleSortingOrderChange}
                                options={ModerationSortingOptions}
                                value={sortingOrder ?? savedSortingOrder}
                            />
                            <View>
                                <Pad size={16} />
                                {threads ? (
                                    <ModDashboardThreadsOverviewList
                                        authors={authors}
                                        onLoadMore={loadMoreThreads}
                                        remainingThreadCount={remainingThreadCount}
                                        threads={threads}
                                    />
                                ) : (
                                    <NoThreadsBanner />
                                )}
                            </View>
                        </View>
                    </PadBox>
                </View>
            )}
        </>
    );

    return <>{renderedContent}</>;
}

export function NoThreadsBanner() {
    return (
        // TODO: This used to be fit-content but adding a style breaks typing. Now the banner is too wide
        <Banner color={colorSurfacePrimary}>
            <UtilityText label={t('No threads')} />
        </Banner>
    );
}

type ModDashboardOverviewListProps = {
    threads: QuestionOrTopicThread[];
    authors: Record<string, Persona>;
    remainingThreadCount?: number;
    onLoadMore: () => void;
};

export type ModDashboardThreadsOverviewListProps = ModDashboardOverviewListProps;

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ModDashboardThreadsOverviewList({
    threads,
    authors,
    remainingThreadCount = 0,
    onLoadMore,
}: ModDashboardThreadsOverviewListProps) {
    const params = useInstanceParams();
    const instanceKey = useInstanceKey();
    const datastore = useDatastore();

    const openThreadDetails = (threadId) => {
        params.threadId = threadId;
        datastore.gotoInstance({ structureKey: 'moddashboard', instanceKey, params: params });
    };

    const loadMoreItems = () => {
        onLoadMore && onLoadMore();
    };

    return (
        <View style={{ gap: 16 }}>
            {threads && authors ? (
                threads.length > 0 ? (
                    <View style={{ gap: 16 }}>
                        {threads.map((thread, index) => (
                            <ModDashboardThreadCard
                                author={authors?.[thread?.from]}
                                key={thread.key + index}
                                onPressCard={() => {
                                    openThreadDetails(thread.key);
                                }}
                                secondaryTags={thread.structureKey === 'topic' ? [{ key: 'topic', label: t('Topic') }] : undefined}
                                thread={thread}
                            />
                        ))}
                        {remainingThreadCount > 0 && (
                            <Center>
                                <CTAButton label={t('Load more')} onPress={loadMoreItems}></CTAButton>
                            </Center>
                        )}
                    </View>
                ) : (
                    <NoThreadsBanner />
                )
            ) : (
                <LoadingScreen />
            )}
        </View>
    );
}

type ModDashboardThreadCardProps = {
    thread: QuestionOrTopicThread;
    secondaryTags?: SecondaryThreadTag[];
    author: Persona;
    onPressCard: () => void;
};

export function ModDashboardThreadCard({ thread, secondaryTags = [], author, onPressCard }: ModDashboardThreadCardProps) {
    const s = ThreadCardStyle;

    const threadIsClosed = thread?.readOnly ? true : false;
    const isHeated = thread.isHeated || false;

    return (
        <HoverView onPress={onPressCard}>
            <View style={s.cardContainer}>
                <HorizBox spread>
                    <View style={s.tagRow}>
                        <Tag
                            color={threadIsClosed ? colorTagHighlight : colorTagSuccess}
                            compact
                            label={threadIsClosed ? t('Closed') : t('Open')}
                        />
                        {secondaryTags
                            .filter((tag) => tag != null)
                            .map((tag, index) => (
                                <Tag
                                    borderColor={colorBorderPrimary}
                                    color={colorSurfacePrimary}
                                    compact
                                    key={tag.key + index}
                                    label={tag.label}
                                    textColor={colorTextPrimary}
                                />
                            ))}
                    </View>
                    <View style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                        {isHeated && (
                            <PadBox left={8}>
                                <Tag color={'#FCEBEA'} compact emoji={'🔥'} label={t('Heated')} />
                            </PadBox>
                        )}
                    </View>
                </HorizBox>
                <View style={s.mainContentRow}>
                    <View style={s.titleBlock}>
                        <Paragraph numberOfLines={1} strong text={thread.question ?? thread.name} />
                        <UtilityText
                            color={colorTextSecondary}
                            formatParams={{ author: author?.name }}
                            label={t('Author: {author}')}
                            type='tiny'
                        />
                    </View>
                </View>
                <ModDashboardThreadMetaData thread={thread} threadKey={thread.key} />
            </View>
        </HoverView>
    );
}

const ThreadCardStyle = StyleSheet.create({
    cardContainer: {
        borderRadius: 10,
        padding: 16,
        gap: 16,
        backgroundColor: colorSurfacePrimary,
        /** @ts-ignore FIXME: boxShadow not typed for native */
        boxShadow: ' 0px 2px 10px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
    },
    tagRow: {
        flexDirection: 'row',
        gap: 12,
    },
    image: {
        width: 56,
        height: 56,
    },
    mainContentRow: {
        flexDirection: 'row',
        gap: 12,
        alignItems: 'center',
    },
    titleBlock: {
        gap: 4,
        width: '100%',
    },
    detailRow: {
        gap: 16,
        flexDirection: 'row',
    },
    detailItem: {
        gap: 4,
        flexDirection: 'row',
        color: colorTextSecondary,
    },
});

const backlinkArticleTable = declareTable<BacklinkArticle>('backlink_article');

export type ModDashboardThreadDetailsProps = { includeTabs?: boolean };

export function ModDashboardThreadDetails({ includeTabs = true }: ModDashboardThreadDetailsProps) {
    const threadKey = useInstanceKey();
    const canView = useHasCapability(MODDASHBOARD_CAPABILITY);

    // Determine if you are a question or topic
    const question = DEPRECATED_useModulePublicData('question', [threadKey]);
    const topic = DEPRECATED_useModulePublicData('topic', [threadKey]);
    const thread = question ?? topic;

    const threadAuthor = usePersonaObject(thread?.from);

    const articles = useCollection(backlinkArticleTable);

    if (!canView) {
        return (
            <Banner>
                <UtilityText label={t('You do not have access to this feature')} />
            </Banner>
        );
    }

    if (!thread) {
        return <LoadingScreen />;
    }

    return (
        <PadBox top={80}>
            <SpacedArray pad={8}>
                <Heading text={thread?.question ?? thread.name} type={'large'} weight='medium' />
                <UtilityText
                    color={colorTextSecondary}
                    formatParams={{ author: threadAuthor?.name }}
                    label={t('Author: {author}')}
                    type='tiny'
                />
            </SpacedArray>
            <Pad size={24} />
            <ModDashboardThreadMetaData articles={articles} thread={thread} threadKey={threadKey} />
            <Pad size={56} />
            {includeTabs && <ModDashboardThreadDetailTabs thread={thread} threadKey={threadKey} />}
        </PadBox>
    );
}

type ModDashboardThreadMetaDataProps = {
    thread: QuestionOrTopicThread;
    threadKey: string;
    articles?: BacklinkArticle[];
};

function ModDashboardThreadMetaData({ thread, threadKey, articles }: ModDashboardThreadMetaDataProps) {
    const commentCount = thread.commentCount;
    const awaitingDecisionCount = DEPRECATED_useModulePublicData('moderation', [
        'thread_queue_count',
        threadKey,
        'awaiting_decision_count',
    ]);
    const [display, shouldDisplay] = useState(false);
    const s = ThreadCardStyle;

    return (
        <View style={s.detailRow}>
            {thread.time && (
                <View style={s.detailItem}>
                    <Time />
                    <DateTimeDisplay color={colorTextSecondary} timestamp={thread.time} type='tiny' />
                </View>
            )}
            {commentCount !== null && (
                <View style={s.detailItem}>
                    <Chat />
                    <UtilityText color={colorTextSecondary} text={commentCount?.toString()} type='tiny' />
                </View>
            )}
            <View style={s.detailItem}>
                <Flag color={awaitingDecisionCount ? colorTextWarning : colorTextSecondary} />
                <UtilityText
                    color={awaitingDecisionCount ? colorTextWarning : colorTextSecondary}
                    text={awaitingDecisionCount !== null ? awaitingDecisionCount : '0'}
                    type='tiny'
                />
            </View>
            {thread.id && (
                <View style={s.detailItem}>
                    <IdManagement />
                    <UtilityText color={colorTextSecondary} text={thread.id} type='tiny' />
                </View>
            )}
            {articles && (
                <View style={s.detailItem}>
                    <Link color={colorTextAccent} />
                    <TextButton
                        color={colorTextAccent}
                        formatParams={{ numArticles: articles.length }}
                        label={t('{numArticles} linked stories')}
                        onPress={() => shouldDisplay(true)}
                        type='tiny'
                    />
                    {display && articles.length > 0 && (
                        <ModDashboardThreadArticleModal articles={articles} onClose={() => shouldDisplay(false)} />
                    )}
                </View>
            )}
        </View>
    );
}

export type ModDashboardThreadArticleModalProps = { onClose: () => void; articles: BacklinkArticle[] };

export function ModDashboardThreadArticleModal({ onClose, articles }: ModDashboardThreadArticleModalProps) {
    return (
        <Modal onClose={onClose}>
            <View style={{ paddingVertical: 40, paddingHorizontal: 20, gap: 8 }}>
                <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
                    <UtilityText label={t('Linked stories')} type='small' weight='strong' />
                    <Pad size={8} />
                    <View
                        style={{
                            width: 25,
                            height: 25,
                            borderRadius: '50%',
                            backgroundColor: colorButtonDisabled,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <UtilityText center text={articles.length.toString()} type='small' weight='strong' />
                    </View>
                </View>
                <Pad />
                <View style={{ gap: 8 }}>
                    {articles.map((article, index) => (
                        <ModDashboardThreadArticleCard article={article} key={article.key + index} />
                    ))}
                </View>
            </View>
        </Modal>
    );
}

function ModDashboardThreadArticleCard({ article }: { article: ArticleInfo & { key: string } }) {
    const s = ArticleCardThreadStyle;

    return (
        <View style={s.outer}>
            <View style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <HorizBox center>
                    <Image src={article.image} style={s.image} />
                    <Pad size={4} />
                    <View style={s.articleList}>
                        <Paragraph color={colorTextSecondary} text={article.title} />
                        <REPLACE_ZDF_LinkText
                            label={t('Read article')}
                            strong
                            testID={`Read ${article.title}`}
                            type='small'
                            url={keyToUrl(article.key)}
                        />
                    </View>
                </HorizBox>
            </View>
        </View>
    );
}

const ArticleCardThreadStyle = StyleSheet.create({
    outer: {
        backgroundColor: colorSurfaceSecondary,
        borderRadius: 8,
        padding: 12,
    },
    articleList: {
        gap: 4,
        flexShrink: 1,
    },
    image: {
        width: 48,
        height: 48,
        borderRadius: 8,
    },
});

function ModDashboardThreadDetailTabs({ thread, threadKey }) {
    const tabs: Tab[] = useMemo(
        () => [
            {
                id: '0',
                label: t('Comments'),
                component: () => (
                    <ModDashboardQueue
                        additionalFilters={[ModerationFilters.Bookmarked] as any}
                        instanceKey={threadKey}
                        structureKey={'question'}
                        useAllFilteredQueues={useFilteredQueues}
                        // This cast as any will be fixed in an upcoming PR
                        useQueueCounts={getQueueSubsetCounts}
                    />
                ),
            },
        ],
        [thread.key],
    );

    return (
        <View style={{ flexGrow: 1 }}>
            <TabNavigation initialTabId={'0'} tabs={tabs}>
                {/* TODO: We should eventually switch to the core TabBar if possible. However, our version doesn't have the issue of the labels shifting when the bold highlight makes the text larger */}
                <TabBar />
                <TabContent />
            </TabNavigation>
        </View>
    );
}

export function ModDashboardThreadTreeView() {
    const params = useInstanceParams();
    const modTaskKey = params?.modTaskTreeKey;
    const selectedModTask = useModuleObject(moduleMapQueue, modTaskKey);

    if (!selectedModTask) return;

    return (
        <ParentDatastoreProvider>
            <EmbeddedInstance
                features={{ enablemodtasktreeview: true }}
                instanceKey={params?.threadId}
                screenKey={'modTaskTreeView'}
                structureKey={selectedModTask.structureKey}
            />
        </ParentDatastoreProvider>
    );
}
